import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UUID } from "crypto";
import api from "@/app/api";
import {
  inventoryOperationKeys,
  inventoryTransactionKeys,
  inventoryTransferKeys,
} from "../keys/inventory";
import type {
  OperationCreate,
  OperationUpdate,
  OperationFilters,
} from "@/app/api/operations/client";
import type {
  TransactionCreate,
  TransactionUpdate,
  TransactionFilters,
} from "@/app/api/transactions/client";
import type {
  TransferCreate,
  TransferUpdate,
  TransferFilters,
} from "@/app/api/transfers/client";

// ==================== OPERATIONS ====================

export function useInventoryOperations(filters?: OperationFilters) {
  return useQuery({
    queryKey: inventoryOperationKeys.list(filters),
    queryFn: () => api.inventoryOperations.list(filters),
  });
}

export function useInventoryOperationsPaginated(
  filters?: OperationFilters,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: inventoryOperationKeys.list({ ...filters, paginated: true }),
    queryFn: () => api.inventoryOperations.listPaginated(filters),
    enabled: options?.enabled ?? true,
  });
}

export function useInventoryOperation(id: UUID | null) {
  return useQuery({
    queryKey: inventoryOperationKeys.detail(id!),
    queryFn: () => api.inventoryOperations.get(id!),
    enabled: !!id,
  });
}

export function useInventoryOperationWithTransactions(id: UUID | null) {
  return useQuery({
    queryKey: inventoryOperationKeys.withTransactions(id!),
    queryFn: () => api.inventoryOperations.getWithTransactions(id!),
    enabled: !!id,
  });
}

export function useInventoryOperationDetails(id: UUID | null) {
  return useQuery({
    queryKey: inventoryOperationKeys.fullDetails(id!),
    queryFn: () => api.inventoryOperations.getDetails(id!),
    enabled: !!id,
  });
}

export function useInventoryOperationFees(id: UUID | null) {
  return useQuery({
    queryKey: inventoryOperationKeys.fees(id!),
    queryFn: () => api.inventoryOperations.getFees(id!),
    enabled: !!id,
  });
}

export function useCreateInventoryOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OperationCreate) => api.inventoryOperations.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryOperationKeys.all });
    },
  });
}

export function useUpdateInventoryOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: UUID; data: OperationUpdate }) =>
      api.inventoryOperations.update(id, data),
    onSuccess: (operation) => {
      queryClient.invalidateQueries({ queryKey: inventoryOperationKeys.all });
      queryClient.invalidateQueries({
        queryKey: inventoryOperationKeys.detail(operation.id),
      });
    },
  });
}

export function useDeleteInventoryOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.inventoryOperations.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryOperationKeys.all });
    },
  });
}

export function useCompleteInventoryOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.inventoryOperations.complete(id),
    onSuccess: (operation) => {
      queryClient.invalidateQueries({ queryKey: inventoryOperationKeys.all });
      queryClient.invalidateQueries({
        queryKey: inventoryOperationKeys.detail(operation.id),
      });
    },
  });
}

export function useCancelInventoryOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.inventoryOperations.cancel(id),
    onSuccess: (operation) => {
      queryClient.invalidateQueries({ queryKey: inventoryOperationKeys.all });
      queryClient.invalidateQueries({
        queryKey: inventoryOperationKeys.detail(operation.id),
      });
    },
  });
}

// ==================== TRANSACTIONS ====================

export function useInventoryTransactions(filters?: TransactionFilters) {
  return useQuery({
    queryKey: inventoryTransactionKeys.list(filters),
    queryFn: () => api.inventoryTransactions.list(filters),
  });
}

export function useInventoryTransactionsByOperation(operationId: UUID | null) {
  return useQuery({
    queryKey: inventoryTransactionKeys.byOperation(operationId!),
    queryFn: () => api.inventoryTransactions.listByOperation(operationId!),
    enabled: !!operationId,
  });
}

export function useInventoryTransaction(id: UUID | null) {
  return useQuery({
    queryKey: inventoryTransactionKeys.detail(id!),
    queryFn: () => api.inventoryTransactions.get(id!),
    enabled: !!id,
  });
}

export function useInventoryTransactionWithFlows(id: UUID | null) {
  return useQuery({
    queryKey: inventoryTransactionKeys.withFlows(id!),
    queryFn: () => api.inventoryTransactions.getWithFlows(id!),
    enabled: !!id,
  });
}

export function useCreateInventoryTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TransactionCreate) =>
      api.inventoryTransactions.create(data),
    onSuccess: (transaction) => {
      queryClient.invalidateQueries({ queryKey: inventoryTransactionKeys.all });
      if (transaction.operation_id) {
        queryClient.invalidateQueries({
          queryKey: inventoryOperationKeys.detail(transaction.operation_id),
        });
      }
    },
  });
}

export function useUpdateInventoryTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: UUID; data: TransactionUpdate }) =>
      api.inventoryTransactions.update(id, data),
    onSuccess: (transaction) => {
      queryClient.invalidateQueries({ queryKey: inventoryTransactionKeys.all });
      queryClient.invalidateQueries({
        queryKey: inventoryTransactionKeys.detail(transaction.id),
      });
    },
  });
}

export function useDeleteInventoryTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.inventoryTransactions.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryTransactionKeys.all });
      queryClient.invalidateQueries({ queryKey: inventoryOperationKeys.all });
    },
  });
}

export function useCompleteInventoryTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.inventoryTransactions.complete(id),
    onSuccess: (transaction) => {
      queryClient.invalidateQueries({ queryKey: inventoryTransactionKeys.all });
      queryClient.invalidateQueries({
        queryKey: inventoryTransactionKeys.detail(transaction.id),
      });
    },
  });
}

export function useConfirmInventoryTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.inventoryTransactions.confirm(id),
    onSuccess: (transaction) => {
      queryClient.invalidateQueries({ queryKey: inventoryTransactionKeys.all });
      queryClient.invalidateQueries({
        queryKey: inventoryTransactionKeys.detail(transaction.id),
      });
    },
  });
}

export function useCancelInventoryTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.inventoryTransactions.cancel(id),
    onSuccess: (transaction) => {
      queryClient.invalidateQueries({ queryKey: inventoryTransactionKeys.all });
      queryClient.invalidateQueries({
        queryKey: inventoryTransactionKeys.detail(transaction.id),
      });
    },
  });
}

// ==================== TRANSFERS ====================

export function useInventoryTransfers(filters?: TransferFilters) {
  return useQuery({
    queryKey: inventoryTransferKeys.list(filters),
    queryFn: () => api.inventoryTransfers.list(filters),
  });
}

export function useInventoryTransfersByStock(stockId: UUID | null) {
  return useQuery({
    queryKey: inventoryTransferKeys.byStock(stockId!),
    queryFn: () => api.inventoryTransfers.listByStock(stockId!),
    enabled: !!stockId,
  });
}

export function useInventoryTransfer(id: UUID | null) {
  return useQuery({
    queryKey: inventoryTransferKeys.detail(id!),
    queryFn: () => api.inventoryTransfers.get(id!),
    enabled: !!id,
  });
}

export function useInventoryTransferWithRelations(id: UUID | null) {
  return useQuery({
    queryKey: inventoryTransferKeys.withRelations(id!),
    queryFn: () => api.inventoryTransfers.getWithRelations(id!),
    enabled: !!id,
  });
}

export function useCreateInventoryTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TransferCreate) => api.inventoryTransfers.create(data),
    onSuccess: (transfer) => {
      queryClient.invalidateQueries({ queryKey: inventoryTransferKeys.all });
      queryClient.invalidateQueries({ queryKey: ["stocks"] });
      if (transfer.transaction_id) {
        queryClient.invalidateQueries({
          queryKey: inventoryTransactionKeys.detail(transfer.transaction_id),
        });
      }
    },
  });
}

export function useUpdateInventoryTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: UUID; data: TransferUpdate }) =>
      api.inventoryTransfers.update(id, data),
    onSuccess: (transfer) => {
      queryClient.invalidateQueries({ queryKey: inventoryTransferKeys.all });
      queryClient.invalidateQueries({
        queryKey: inventoryTransferKeys.detail(transfer.id),
      });
    },
  });
}

export function useDeleteInventoryTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.inventoryTransfers.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryTransferKeys.all });
      queryClient.invalidateQueries({ queryKey: inventoryTransactionKeys.all });
      queryClient.invalidateQueries({ queryKey: ["stocks"] });
    },
  });
}

export function useStartInventoryTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.inventoryTransfers.start(id),
    onSuccess: (transfer) => {
      queryClient.invalidateQueries({ queryKey: inventoryTransferKeys.all });
      queryClient.invalidateQueries({
        queryKey: inventoryTransferKeys.detail(transfer.id),
      });
      queryClient.invalidateQueries({ queryKey: ["stocks"] });
    },
  });
}

export function useCompleteInventoryTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.inventoryTransfers.complete(id),
    onSuccess: (transfer) => {
      queryClient.invalidateQueries({ queryKey: inventoryTransferKeys.all });
      queryClient.invalidateQueries({
        queryKey: inventoryTransferKeys.detail(transfer.id),
      });
      queryClient.invalidateQueries({ queryKey: ["stocks"] });
    },
  });
}

export function useCancelInventoryTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.inventoryTransfers.cancel(id),
    onSuccess: (transfer) => {
      queryClient.invalidateQueries({ queryKey: inventoryTransferKeys.all });
      queryClient.invalidateQueries({
        queryKey: inventoryTransferKeys.detail(transfer.id),
      });
      queryClient.invalidateQueries({ queryKey: ["stocks"] });
    },
  });
}
